import { reclaimCompetitorSignalSchema } from "@redotech/redo-dispute-common/reclaim-competitor-signal";
import {
  DEFAULT_RECLAIM_ALERT_PRICE_PER_CASE_USD,
  DEFAULT_RECLAIM_DEFLECTION_BILLABLE_EVENT_MULTIPLIER,
  DEFAULT_RECLAIM_DEFLECTION_CHARGEBACK_PREVENTION_RATE,
  DEFAULT_RECLAIM_DEFLECTION_PRICE_PER_EVENT_USD,
  DEFAULT_RECLAIM_REVENUE_SHARE,
} from "@redotech/redo-dispute-common/reclaim-deal-size";
import { vampProxyRiskLevels } from "@redotech/redo-dispute-common/vamp-proxy";
import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod";

export const reclaimMerchantOpportunityStatus = z.enum([
  "not_live",
  "paused",
  "live",
]);
export type ReclaimMerchantOpportunityStatus = z.infer<
  typeof reclaimMerchantOpportunityStatus
>;

export const reclaimMerchantOpportunitySortKey = z.enum([
  "teamName",
  "chargebackVolumeUsdTrailing365d",
  "chargebackCountTrailing365d",
  "averageChargebackAmountUsdTrailing365d",
  "vampProxyRatioTrailing30d",
  "estimatedTotalRevenueUsd",
  "estimatedDeflectionRevenueUsd",
  "estimatedAlertRevenueUsd",
  "estimatedRepresentmentRevenueUsd",
  "residualChargebackVolumeUsd",
  "reclaimStatus",
]);
export type ReclaimMerchantOpportunitySortKey = z.infer<
  typeof reclaimMerchantOpportunitySortKey
>;

export const reclaimMerchantOpportunitySortDirection = z.enum(["asc", "desc"]);
export type ReclaimMerchantOpportunitySortDirection = z.infer<
  typeof reclaimMerchantOpportunitySortDirection
>;

export const reclaimMerchantOpportunityCompetitorStatusFilter = z.enum([
  "matched",
]);
export type ReclaimMerchantOpportunityCompetitorStatusFilter = z.infer<
  typeof reclaimMerchantOpportunityCompetitorStatusFilter
>;

export const reclaimMerchantOpportunityDealSizeBasis = z.enum([
  "actual_365d",
  "projected_annual",
]);
export type ReclaimMerchantOpportunityDealSizeBasis = z.infer<
  typeof reclaimMerchantOpportunityDealSizeBasis
>;

export const reclaimMerchantOpportunitySortSchema = z.object({
  key: reclaimMerchantOpportunitySortKey,
  dir: reclaimMerchantOpportunitySortDirection,
});
export interface ReclaimMerchantOpportunitySort extends z.infer<
  typeof reclaimMerchantOpportunitySortSchema
> {}

const rateAssumptionSchema = z.number().min(0).max(1);
const usdAssumptionSchema = z.number().min(0);
const nonNegativeAssumptionSchema = z.number().min(0);

export const reclaimOpportunityWaterfallAssumptionsSchema = z.object({
  includeRepresentment: z.boolean().default(true),
  includeDeflection: z.boolean().default(true),
  includeAlerts: z.boolean().default(true),
  representmentRevenueShare: rateAssumptionSchema.default(
    DEFAULT_RECLAIM_REVENUE_SHARE,
  ),
  representmentWinRate: rateAssumptionSchema.default(0),
  representmentEligibleRate: rateAssumptionSchema.default(1),
  deflectionChargebackPreventionRate: rateAssumptionSchema.default(
    DEFAULT_RECLAIM_DEFLECTION_CHARGEBACK_PREVENTION_RATE,
  ),
  deflectionBillableEventMultiplier: nonNegativeAssumptionSchema.default(
    DEFAULT_RECLAIM_DEFLECTION_BILLABLE_EVENT_MULTIPLIER,
  ),
  deflectionPricePerEventUsd: usdAssumptionSchema.default(
    DEFAULT_RECLAIM_DEFLECTION_PRICE_PER_EVENT_USD,
  ),
  alertCoverageRate: rateAssumptionSchema.default(0),
  alertPercentBelowThreshold: rateAssumptionSchema.default(0),
  alertRefundRate: rateAssumptionSchema.default(1),
  alertPreventedChargebackRate: rateAssumptionSchema.default(1),
  alertPricePerCaseUsd: usdAssumptionSchema.default(
    DEFAULT_RECLAIM_ALERT_PRICE_PER_CASE_USD,
  ),
  autoRefundThresholdUsd: usdAssumptionSchema.default(0),
});
export type ReclaimOpportunityWaterfallAssumptions = z.infer<
  typeof reclaimOpportunityWaterfallAssumptionsSchema
>;

export const reclaimOpportunityWaterfallAssumptionsInputSchema =
  reclaimOpportunityWaterfallAssumptionsSchema.partial();
export type ReclaimOpportunityWaterfallAssumptionsInput = z.infer<
  typeof reclaimOpportunityWaterfallAssumptionsInputSchema
>;

export const reclaimOpportunityPreDisputeDataSource = z.enum([
  "inquiry",
  "chargeback_proxy",
]);
export type ReclaimOpportunityPreDisputeDataSource = z.infer<
  typeof reclaimOpportunityPreDisputeDataSource
>;

export const reclaimOpportunityVampProxyRiskLevel = z.enum(vampProxyRiskLevels);
export type ReclaimOpportunityVampProxyRiskLevel = z.infer<
  typeof reclaimOpportunityVampProxyRiskLevel
>;

export const reclaimOpportunityVampProxySchema = z.object({
  chargebackCountTrailing30d: z.number().int(),
  orderCountTrailing30d: z.number().int(),
  ratioTrailing30d: z.number().nullable(),
  thresholdRatio: z.number(),
  minimumMonthlyDisputeCount: z.number().int(),
  riskLevel: reclaimOpportunityVampProxyRiskLevel,
});
export type ReclaimOpportunityVampProxy = z.infer<
  typeof reclaimOpportunityVampProxySchema
>;

export const reclaimOpportunityWaterfallEstimateSchema = z.object({
  baselineChargebackCount: z.number(),
  baselineChargebackVolumeUsd: z.number(),
  preDisputeCount: z.number(),
  preDisputeVolumeUsd: z.number(),
  deflectionPreventedChargebackCount: z.number(),
  billableDeflectionEventCount: z.number(),
  successfulDeflectionCount: z.number(),
  deflectedVolumeUsd: z.number(),
  deflectionRevenueUsd: z.number(),
  alertCaseCount: z.number(),
  autoRefundedAlertCount: z.number(),
  alertRefundedVolumeUsd: z.number(),
  alertPreventedChargebackVolumeUsd: z.number(),
  alertRevenueUsd: z.number(),
  residualChargebackVolumeUsd: z.number(),
  residualEligibleChargebackVolumeUsd: z.number(),
  representmentRevenueUsd: z.number(),
  totalRevenueUsd: z.number(),
});
export type ReclaimOpportunityWaterfallEstimate = z.infer<
  typeof reclaimOpportunityWaterfallEstimateSchema
>;

export const reclaimOpportunityAdditiveComparisonEstimateSchema = z.object({
  representmentRevenueUsd: z.number(),
  totalRevenueUsd: z.number(),
  overstatementUsd: z.number(),
});
export type ReclaimOpportunityAdditiveComparisonEstimate = z.infer<
  typeof reclaimOpportunityAdditiveComparisonEstimateSchema
>;

export const reclaimOpportunityWaterfallSummarySchema = z.object({
  totalRevenueUsd: z.number(),
  deflectionRevenueUsd: z.number(),
  alertRevenueUsd: z.number(),
  representmentRevenueUsd: z.number(),
  residualChargebackVolumeUsd: z.number(),
  additiveOverstatementUsd: z.number(),
});
export type ReclaimOpportunityWaterfallSummary = z.infer<
  typeof reclaimOpportunityWaterfallSummarySchema
>;

export const reclaimMerchantOpportunityRowSchema = z.object({
  teamId: z.string(),
  teamName: z.string(),
  teamLogoUrl: z.string().optional(),
  accountId: z.string(),
  chargebackCountTrailing365d: z.number().int(),
  chargebackVolumeUsdTrailing365d: z.number(),
  averageChargebackAmountUsdTrailing365d: z.number(),
  preDisputeCountTrailing365d: z.number(),
  preDisputeVolumeUsdTrailing365d: z.number(),
  preDisputeDataSource: reclaimOpportunityPreDisputeDataSource,
  vampProxy: reclaimOpportunityVampProxySchema,
  waterfallEstimate: reclaimOpportunityWaterfallEstimateSchema,
  additiveComparisonEstimate:
    reclaimOpportunityAdditiveComparisonEstimateSchema,
  reclaimStatus: reclaimMerchantOpportunityStatus,
  competitorSignal: reclaimCompetitorSignalSchema,
  hasIncompleteFx: z.boolean(),
  skippedCurrencies: z.array(z.string()),
});
export interface ReclaimMerchantOpportunityRow extends z.infer<
  typeof reclaimMerchantOpportunityRowSchema
> {}

const reclaimMerchantOpportunityBaseInputSchema = z.object({
  status: reclaimMerchantOpportunityStatus.optional(),
  competitorStatus: reclaimMerchantOpportunityCompetitorStatusFilter.optional(),
  dealSizeBasis: reclaimMerchantOpportunityDealSizeBasis.default("actual_365d"),
  assumptions: reclaimOpportunityWaterfallAssumptionsInputSchema.optional(),
  searchQuery: z.string().trim().max(200).optional(),
});

const reclaimMerchantOpportunityRowsInputSchema =
  reclaimMerchantOpportunityBaseInputSchema.extend({
    page: z.number().int().min(1).default(1),
    pageSize: z.number().int().min(1).max(500).default(50),
    sort: reclaimMerchantOpportunitySortSchema.optional(),
  });

const reclaimMerchantOpportunityRowsOutputSchema = z.object({
  rows: z.array(reclaimMerchantOpportunityRowSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  totalCount: z.number().int(),
  isCompetitorFilterPartial: z.boolean(),
});

const reclaimMerchantOpportunitySummaryOutputSchema = z.object({
  fxSkippedCurrencies: z.array(z.string()),
  opportunityWaterfallSummary: reclaimOpportunityWaterfallSummarySchema,
  capturedWaterfallSummary: reclaimOpportunityWaterfallSummarySchema,
  isCompetitorFilterPartial: z.boolean(),
});

export const getReclaimMerchantOpportunityRows = unaryRpc({
  name: "getReclaimMerchantOpportunityRows" as const,
  input: reclaimMerchantOpportunityRowsInputSchema,
  output: reclaimMerchantOpportunityRowsOutputSchema,
});

export const getReclaimMerchantOpportunitySummary = unaryRpc({
  name: "getReclaimMerchantOpportunitySummary" as const,
  input: reclaimMerchantOpportunityBaseInputSchema,
  output: reclaimMerchantOpportunitySummaryOutputSchema.extend({
    totalChargebackCount: z.number().int(),
    totalChargebackVolumeUsd: z.number(),
  }),
});

export const getReclaimMerchantOpportunities = unaryRpc({
  name: "getReclaimMerchantOpportunities" as const,
  input: reclaimMerchantOpportunityRowsInputSchema,
  output: reclaimMerchantOpportunityRowsOutputSchema
    .merge(reclaimMerchantOpportunitySummaryOutputSchema)
    .extend({
      skippedMerchantCount: z.number().int(),
      globalWinRateLast30d: z.number(),
      waterfallSummary: reclaimOpportunityWaterfallSummarySchema,
    }),
});
